import React, { useEffect, useState } from 'react'
import { sendEmail } from '../emailService'
import { supabase } from '../supabaseClient'
import { POLICY_SECTIONS, checkoutUrl } from '../policies'

const BUSINESS_NAME = 'UniEDD Music & Arts Academy'

const FOREIGN_CURRENCIES = [
  { code:'USD', symbol:'$',   label:'USD — US Dollar'          },
  { code:'GBP', symbol:'£',   label:'GBP — British Pound'      },
  { code:'EUR', symbol:'€',   label:'EUR — Euro'               },
  { code:'AED', symbol:'د.إ', label:'AED — UAE Dirham'         },
  { code:'SGD', symbol:'S$',  label:'SGD — Singapore Dollar'   },
  { code:'AUD', symbol:'A$',  label:'AUD — Australian Dollar'  },
  { code:'CAD', symbol:'C$',  label:'CAD — Canadian Dollar'    },
]

export default function PaymentsAdmin({ profile }) {
  const [payments,  setPayments]  = useState([])
  const [students,  setStudents]  = useState([])
  const [loading,   setLoading]   = useState(true)
  const [filter,    setFilter]    = useState('all')
  const [tab,       setTab]       = useState('list')
  const [mode,      setMode]      = useState('international') // international | india
  const [ok,        setOk]        = useState('')
  const [err,       setErr]       = useState('')
  const [busy,      setBusy]      = useState(false)
  const [copied,    setCopied]    = useState(null)
  const [generated, setGenerated] = useState(null)
  const [previewInvoice, setPreviewInvoice] = useState(null)

  // Form
  const [selStudent, setSelStudent] = useState('')
  const [courseName, setCourseName] = useState('')
  const [amount,     setAmount]     = useState('')
  const [currency,   setCurrency]   = useState('USD')
  const [dueDate,    setDueDate]    = useState('')
  const [notes,      setNotes]      = useState('')
  const [pasteLink,  setPasteLink]  = useState('')
  // Manual payment fields
  const [manualMethod,  setManualMethod]  = useState('UPI')
  const [manualRef,     setManualRef]     = useState('')
  const [manualDate,    setManualDate]    = useState('')

  useEffect(() => { load() }, [])

  async function load() {
    setLoading(true)
    const [{ data: pays }, { data: studs }] = await Promise.all([
      supabase.from('payments').select('*').order('created_at', { ascending: false }),
      supabase.from('profiles').select('id,full_name,email,phone,student_id').eq('role','student'),
    ])
    setPayments(pays || [])
    setStudents(studs || [])
    setLoading(false)
  }

  function getSymbol(code) {
    return FOREIGN_CURRENCIES.find(c => c.code === code)?.symbol || '$'
  }

  // ── Generate PayPal link (international) ──────────────────
  async function generatePayPal(e) {
    e.preventDefault()
    setErr(''); setOk(''); setBusy(true); setGenerated(null)
    const student = students.find(s => s.id === selStudent)
    const amtNum  = parseFloat(amount)
    if (!selStudent)                           { setErr('Please select a student.');     setBusy(false); return }
    if (!courseName.trim())                    { setErr('Please enter course name.');    setBusy(false); return }
    if (!amount || isNaN(amtNum) || amtNum<=0) { setErr('Please enter a valid amount.'); setBusy(false); return }

    const invoiceNo = 'INV-' + Date.now().toString().slice(-6)
    const desc      = `${courseName.trim()} — ${student?.full_name} | ${invoiceNo}`

    try {
      const rawLink = pasteLink.trim()
      if (rawLink && !isValidUrl(rawLink)) {
        setErr('Please paste a valid payment URL or leave the field empty.')
        setBusy(false)
        return
      }

      let paymentLink = rawLink || null
      let gateway = paymentLink ? 'manual_link' : 'paypal'
      let data = { success: true, paymentLink }

      if (!paymentLink) {
        const { data: { session } } = await supabase.auth.getSession()
        const res = await fetch('/api/create-paypal-link', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ amount: amtNum, currency, description: desc, studentName: student?.full_name, studentEmail: student?.email, invoiceNo, accessToken: session?.access_token }),
        })
        data = await res.json()
        if (!data.success) throw new Error(data.error || 'Failed')
        paymentLink = data.paymentLink
      }

      const { error } = await supabase.from('payments').insert({
        student_id:    selStudent,
        student_name:  student?.full_name,
        student_email: student?.email || null,
        course_name:   courseName.trim(),
        amount:        amtNum,
        currency,
        status:        'pending',
        invoice_no:    invoiceNo,
        payment_link:  paymentLink,
        gateway,
        due_date:      dueDate || null,
        generated_by:  profile.full_name,
        notes:         notes.trim() || null,
      })
      if (error) throw new Error(error.message)

      const generatedData = {
        // Parents open UniEDD's checkout page first and agree to the policies;
        // the payments row keeps the raw gateway link.
        link: checkoutUrl({ link: paymentLink, amount: amtNum, currency, course: courseName.trim(), invoiceNo, studentName: student?.full_name }),
        invoiceNo,
        studentName: student?.full_name,
        amount: amtNum,
        currency,
        course: courseName.trim(),
        type: gateway,
        status: 'pending',
      }
      setGenerated(generatedData)
      setPreviewInvoice(generatedData)
      setOk(paymentLink ? `✓ Payment link saved — ${invoiceNo}` : `✓ PayPal link generated — ${invoiceNo}`)
      // Send payment link email + WhatsApp to student
      if (student?.email && paymentLink) {
        sendEmail('payment_link', student.email, {
          name: student.full_name, courseName: courseName.trim(),
          amount: amtNum, currency, paymentLink: generatedData.link,
          invoiceNo, dueDate: dueDate || null,
        })
      }
      // WhatsApp payment-link send disabled for now (2026-08-22, at user's
      // request) — payment_link_wa was never confirmed created/approved in
      // Meta WhatsApp Manager. Email above still goes out. Re-enable once
      // the template is confirmed live:
      // if (student?.phone && paymentLink) {
      //   sendWhatsApp('payment_link_wa', student.phone, [
      //     student.full_name, `${currency} ${amtNum}`, courseName.trim(), paymentLink,
      //   ])
      // }
      resetForm(); load()
    } catch(e) { setErr('⚠ ' + e.message) }
    setBusy(false)
  }

  // ── Record manual payment (India) ─────────────────────────
  async function recordManual(e) {
    e.preventDefault()
    setErr(''); setOk(''); setBusy(true); setGenerated(null)
    const student = students.find(s => s.id === selStudent)
    const amtNum  = parseFloat(amount)
    if (!selStudent)                           { setErr('Please select a student.');     setBusy(false); return }
    if (!courseName.trim())                    { setErr('Please enter course name.');    setBusy(false); return }
    if (!amount || isNaN(amtNum) || amtNum<=0) { setErr('Please enter a valid amount.'); setBusy(false); return }

    const invoiceNo = 'INV-' + Date.now().toString().slice(-6)
    const isPaid    = !!manualRef  // if reference provided, mark as paid

    const { error } = await supabase.from('payments').insert({
      student_id:    selStudent,
      student_name:  student?.full_name,
      student_email: student?.email || null,
      course_name:   courseName.trim(),
      amount:        amtNum,
      currency:      'INR',
      status:        isPaid ? 'paid' : 'pending',
      invoice_no:    invoiceNo,
      payment_link:  null,
      gateway:       'manual',
      due_date:      dueDate || null,
      paid_date:     isPaid ? (manualDate || new Date().toISOString().slice(0,10)) : null,
      generated_by:  profile.full_name,
      notes:         [
        notes.trim(),
        manualMethod && `Method: ${manualMethod}`,
        manualRef    && `Reference: ${manualRef}`,
      ].filter(Boolean).join('\n') || null,
    })

    if (error) { setErr(error.message); setBusy(false); return }

    const generatedData = {
      invoiceNo,
      studentName: student?.full_name,
      amount: amtNum,
      currency: 'INR',
      course: courseName.trim(),
      type: 'manual',
      status: isPaid ? 'paid' : 'pending',
      method: manualMethod,
      ref: manualRef,
      notes: notes.trim(),
    }
    setGenerated(generatedData)
    setPreviewInvoice(generatedData)
    setOk(`✓ Payment recorded — ${invoiceNo} (${isPaid ? 'Paid' : 'Pending'})`)
    resetForm(); load()
    setBusy(false)
  }

  function resetForm() {
    setSelStudent(''); setCourseName(''); setAmount(''); setCurrency('USD')
    setDueDate(''); setNotes(''); setPasteLink(''); setManualMethod('UPI'); setManualRef(''); setManualDate('')
  }

  function copyText(text, key) {
    navigator.clipboard.writeText(text)
    setCopied(key); setTimeout(() => setCopied(null), 2500)
  }

  function isValidUrl(value) {
    if (!value) return false
    try {
      const url = new URL(value)
      return url.protocol === 'http:' || url.protocol === 'https:'
    } catch (_) {
      return false
    }
  }

  function qrCodeSrc(value) {
    return `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(value)}`
  }

  const payLink = p => checkoutUrl({ link: p.payment_link, amount: p.amount, currency: p.currency, course: p.course_name, invoiceNo: p.invoice_no, studentName: p.student_name })

  function whatsappMsg(link, studentName, amount, curr, course) {
    const sym = getSymbol(curr)
    const msg = `Hello ${studentName}! 👋\n\nYour fee for *${course}* at *${BUSINESS_NAME}* is due.\n\n💰 Amount: *${sym}${amount} ${curr}*\n\n💳 Pay here:\n${link}\n\nPlease read and agree to our class & refund policies on that page before paying.\n\nThank you! 🙏`
    return `https://wa.me/?text=${encodeURIComponent(msg)}`
  }

  function downloadReport() {
    if (!payments.length) return
    const rows = [
      ['Invoice No','Student','Email','Course','Amount','Currency','Status','Gateway','Due Date','Paid Date','Created At','Notes'],
      ...payments.map(p => [
        p.invoice_no || '',
        p.student_name || '',
        p.student_email || '',
        p.course_name || '',
        p.amount != null ? p.amount : '',
        p.currency || '',
        p.status || '',
        p.gateway || '',
        p.due_date || '',
        p.paid_date || '',
        p.created_at || '',
        (p.notes || '').replace(/\n/g, ' | '),
      ].map(v => `"${String(v).replace(/"/g, '""')}"`))
    ]
    const csv = rows.map(r => r.join(',')).join('\r\n')
    const blob = new Blob([csv], { type:'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `uniedd-payments-report-${new Date().toISOString().slice(0,10)}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  async function markPaid(id) {
    await supabase.from('payments').update({ status:'paid', paid_date: new Date().toISOString().slice(0,10) }).eq('id', id)
    // Send receipt email + WhatsApp
    const p = payments.find(pay => pay.id === id)
    if (p?.student_email) {
      sendEmail('payment_receipt', p.student_email, {
        name: p.student_name, courseName: p.course_name,
        amount: p.amount, currency: p.currency || 'USD',
        invoiceNo: p.invoice_no, paidDate: new Date().toISOString().slice(0,10),
      })
    }
    // WhatsApp payment-confirmation send disabled for now (2026-08-22, at
    // user's request) — payment_confirmation_wa was never created in Meta
    // WhatsApp Manager (confirmed dead template, see api/razorpay.js's
    // comment — a different flow was already switched off it). Email
    // receipt above still goes out. Re-enable once a real template exists:
    // if (p?.student_id) {
    //   const { data: stu } = await supabase.from('profiles').select('phone').eq('id', p.student_id).single()
    //   if (stu?.phone) {
    //     sendWhatsApp('payment_confirmation_wa', stu.phone, [
    //       p.student_name, `${p.currency || 'USD'} ${p.amount}`, p.course_name,
    //     ])
    //   }
    // }
    setOk('✓ Marked as paid · Receipt email sent'); setTimeout(() => setOk(''), 3000); load()
  }

  async function deletePay(id) {
    if (!window.confirm('Delete this payment record?')) return
    await supabase.from('payments').delete().eq('id', id); load()
  }

  function printInvoice(invoice) {
    const logo = `${window.location.origin}/icon-192.png`
    const policy = `<div class="policy"><div class="policy-head"><img src="${logo}" alt="UniEDD" width="28" height="28"><strong>UniEDD Policies</strong></div>${POLICY_SECTIONS.map(s => `<p><strong>${s.title}:</strong> ${s.text}</p>`).join('')}</div>`
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Invoice ${invoice.invoiceNo}</title><style>body{font-family:Arial,sans-serif;color:#111} .container{max-width:640px;margin:0 auto;padding:24px} .header{margin-bottom:24px;display:flex;align-items:center;gap:14px} .header img{border-radius:10px} .title{font-size:28px;font-weight:800;margin-bottom:8px} .meta{color:#555;margin-bottom:20px} .policy{margin-top:22px;padding-top:12px;border-top:2px solid #e87c1e;font-size:10.5px;line-height:1.45;color:#444;page-break-inside:avoid} .policy-head{display:flex;align-items:center;gap:8px;font-size:13px;color:#111;margin-bottom:6px} .policy-head img{border-radius:6px} .policy p{margin:5px 0} .block{margin-bottom:18px} .label{font-size:12px;color:#777;text-transform:uppercase;letter-spacing:.06em;margin-bottom:6px;display:block} .value{font-size:15px;color:#222} .qr{max-width:220px;border:1px solid #ddd;border-radius:16px;padding:12px;margin-top:16px;text-align:center} .line{border-top:1px solid #ddd;margin:18px 0}</style></head><body><div class="container"><div class="header"><img src="${logo}" alt="UniEDD" width="56" height="56"><div><div class="title">Invoice ${invoice.invoiceNo}</div><div class="meta">${BUSINESS_NAME}</div></div></div><div class="block"><span class="label">Student</span><div class="value">${invoice.studentName || '—'}</div></div><div class="block"><span class="label">Course</span><div class="value">${invoice.course || '—'}</div></div><div class="block"><span class="label">Amount</span><div class="value">${invoice.currency==='INR' ? '₹' + invoice.amount : invoice.currency + ' ' + invoice.amount}</div></div><div class="block"><span class="label">Status</span><div class="value">${invoice.status || 'Pending'}</div></div><div class="block"><span class="label">Payment Type</span><div class="value">${invoice.type === 'paypal' ? 'PayPal Link' : 'Manual Payment'}</div></div>${invoice.method ? `<div class="block"><span class="label">Method</span><div class="value">${invoice.method}</div></div>` : ''}${invoice.ref ? `<div class="block"><span class="label">Reference</span><div class="value">${invoice.ref}</div></div>` : ''}${invoice.notes ? `<div class="block"><span class="label">Notes</span><div class="value">${invoice.notes.replace(/\n/g,'<br>')}</div></div>` : ''}${invoice.link ? `<div class="block"><span class="label">Payment Link</span><div class="value">${invoice.link}</div></div><div class="qr"><img src="https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(invoice.link)}" alt="QR code"></div>` : ''}<div class="line"></div><div style="font-size:12px;color:#555;">Generated on ${new Date().toLocaleString()}</div>${policy}</div></body></html>`
    const win = window.open('', '_blank')
    if (!win) return
    win.document.write(html)
    win.document.close()
    win.focus()
    // Wait for the logo so it's in the PDF.
    const go = () => { win.print(); win.close() }
    const imgs = [...win.document.images]
    let left = imgs.length
    if (!left) return go()
    imgs.forEach(img => { if (img.complete) { if (--left === 0) go() } else img.onload = img.onerror = () => { if (--left === 0) go() } })
  }

  const filtered  = filter === 'all' ? payments : payments.filter(p => p.status === filter)
  const totalPaid = payments.filter(p=>p.status==='paid').reduce((a,p)=>a+(p.amount||0),0)
  const totalDue  = payments.filter(p=>['pending','overdue'].includes(p.status)).reduce((a,p)=>a+(p.amount||0),0)

  const SC = { paid:'#10b981', pending:'#f4a335', overdue:'#f87171' }
  const SB = { paid:'rgba(16,185,129,0.12)', pending:'rgba(232,124,30,0.12)', overdue:'rgba(239,68,68,0.12)' }
  const inp = { width:'100%', background:'rgba(var(--fg-rgb),0.05)', border:'0.5px solid rgba(var(--fg-rgb),0.1)', borderRadius:'10px', padding:'11px 13px', fontSize:'14px', color:'rgba(var(--fg-rgb),var(--a85))', outline:'none', fontFamily:'inherit', boxSizing:'border-box' }
  const lbl = { display:'block', fontSize:'10px', fontWeight:700, color:'rgba(var(--fg-rgb),var(--a32))', letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:'6px', marginTop:'14px' }

  return (
    <div id="uniedd-payments" style={{ marginTop:'14px' }}>

      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'14px', flexWrap:'wrap', gap:'8px' }}>
        <div style={{ fontSize:'16px', fontWeight:700, color:'rgba(var(--fg-rgb),var(--a85))' }}>💳 Payments</div>
        <div style={{ display:'flex', gap:'6px', flexWrap:'wrap' }}>
          {[['list','📋 All Payments'],['generate','+ New Payment'],['reports','📊 Reports']].map(([t,l]) => (
            <button key={t} type="button" onClick={()=>{ setTab(t); setGenerated(null); setOk(''); setErr('') }}
              style={{ fontSize:'12px', fontWeight:600, padding:'7px 14px', borderRadius:'10px', border:'none', cursor:'pointer', fontFamily:'inherit', background:tab===t?'#1e90ff':'rgba(var(--fg-rgb),0.07)', color:tab===t?'#fff':'rgba(var(--fg-rgb),var(--a50))' }}>
              {l}
            </button>
          ))}
        </div>
      </div>

      {/* Summary */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))', gap:'10px', marginBottom:'14px' }}>
        {[
          { label:'Collected', value:`₹${totalPaid.toLocaleString('en-IN')} / $${payments.filter(p=>p.status==='paid'&&p.currency!=='INR').reduce((a,p)=>a+(p.amount||0),0).toFixed(2)}`, color:'#10b981', bg:'rgba(16,185,129,0.1)', bd:'rgba(16,185,129,0.2)' },
          { label:'Pending',   value:payments.filter(p=>['pending','overdue'].includes(p.status)).length + ' records', color:'#f4a335', bg:'rgba(232,124,30,0.1)', bd:'rgba(232,124,30,0.2)' },
          { label:'Total',     value:payments.length + ' records', color:'#5aabff', bg:'rgba(30,144,255,0.1)', bd:'rgba(30,144,255,0.2)' },
        ].map(c=>(
          <div key={c.label} style={{ background:c.bg, border:`0.5px solid ${c.bd}`, borderRadius:'12px', padding:'12px 14px' }}>
            <div style={{ fontSize:'10px', fontWeight:700, color:c.color, letterSpacing:'0.08em', textTransform:'uppercase', marginBottom:'4px' }}>{c.label}</div>
            <div style={{ fontSize:'16px', fontWeight:800, color:'var(--fg-solid)' }}>{c.value}</div>
          </div>
        ))}
      </div>

      {/* ══ GENERATE TAB ══ */}
      {tab === 'generate' && (
        <div style={{ background:'rgba(var(--fg-rgb),0.04)', border:'0.5px solid rgba(var(--fg-rgb),0.07)', borderRadius:'14px', padding:'1.2rem' }}>

          {/* Mode selector */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px', marginBottom:'20px' }}>
            <button type="button" onClick={()=>{ setMode('international'); setCurrency('USD') }} style={{ padding:'14px', borderRadius:'12px', border:`1.5px solid ${mode==='international'?'#1877f2':'rgba(var(--fg-rgb),0.08)'}`, background:mode==='international'?'rgba(24,119,242,0.12)':'rgba(var(--fg-rgb),0.03)', cursor:'pointer', textAlign:'left', transition:'all 0.15s' }}>
              <div style={{ fontSize:'22px', marginBottom:'6px' }}>🌍</div>
              <div style={{ fontSize:'13px', fontWeight:700, color:mode==='international'?'var(--fg-solid)':'rgba(var(--fg-rgb),var(--a50))' }}>International Student</div>
              <div style={{ fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a30))', marginTop:'3px' }}>UAE · US · UK · SG · AU</div>
              <div style={{ fontSize:'10px', fontWeight:700, color:'#5aabff', marginTop:'6px' }}>🅿️ Auto PayPal Link</div>
            </button>
            <button type="button" onClick={()=>{ setMode('india'); setCurrency('INR') }} style={{ padding:'14px', borderRadius:'12px', border:`1.5px solid ${mode==='india'?'#f4a335':'rgba(var(--fg-rgb),0.08)'}`, background:mode==='india'?'rgba(244,163,53,0.1)':'rgba(var(--fg-rgb),0.03)', cursor:'pointer', textAlign:'left', transition:'all 0.15s' }}>
              <div style={{ fontSize:'22px', marginBottom:'6px' }}>🇮🇳</div>
              <div style={{ fontSize:'13px', fontWeight:700, color:mode==='india'?'var(--fg-solid)':'rgba(var(--fg-rgb),var(--a50))' }}>Indian Student</div>
              <div style={{ fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a30))', marginTop:'3px' }}>UPI · Cash · Bank Transfer</div>
              <div style={{ fontSize:'10px', fontWeight:700, color:'#f4a335', marginTop:'6px' }}>📝 Manual Record</div>
            </button>
          </div>

          <form onSubmit={mode === 'international' ? generatePayPal : recordManual}>

            {/* Common fields */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' }}>
              <div>
                <label style={lbl}>Student *</label>
                <select style={inp} value={selStudent} onChange={e=>setSelStudent(e.target.value)} required>
                  <option value="">— Select student —</option>
                  {students.map(s=><option key={s.id} value={s.id}>{s.full_name}{s.email?` (${s.email})`:''}</option>)}
                </select>
              </div>
              <div>
                <label style={lbl}>Course / Description *</label>
                <input style={inp} type="text" placeholder="e.g. Guitar — 3 months" value={courseName} onChange={e=>setCourseName(e.target.value)} required />
              </div>
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' }}>
              <div>
                <label style={lbl}>Amount * {mode==='india'?'(₹ INR)':'(USD)'}</label>
                <div style={{ position:'relative' }}>
                  <span style={{ position:'absolute', left:'13px', top:'50%', transform:'translateY(-50%)', color:'rgba(var(--fg-rgb),var(--a40))', fontSize:'14px' }}>{mode==='india'?'₹':'$'}</span>
                  <input style={{ ...inp, paddingLeft:'28px' }} type="number" placeholder={mode==='india'?'5000':'60'} min="1" step={mode==='india'?'1':'0.01'} value={amount} onChange={e=>setAmount(e.target.value)} required />
                </div>
              </div>
              {mode === 'international' ? (
                <>
                  <div>
                    <label style={lbl}>Currency</label>
                    <select style={inp} value={currency} onChange={e=>setCurrency(e.target.value)}>
                      {FOREIGN_CURRENCIES.map(c=><option key={c.code} value={c.code}>{c.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={lbl}>Existing Payment Link (optional)</label>
                    <input style={inp} type="url" placeholder="Paste PayPal or other payment link here" value={pasteLink} onChange={e=>setPasteLink(e.target.value)} />
                    <div style={{ fontSize:'10px', color:'rgba(var(--fg-rgb),var(--a45))', marginTop:'6px' }}>
                      Enter or paste an existing payment link, and the system will save it instead of generating a new one.
                    </div>
                  </div>
                </>
              ) : (
                <div>
                  <label style={lbl}>Payment Method</label>
                  <select style={inp} value={manualMethod} onChange={e=>setManualMethod(e.target.value)}>
                    <option value="UPI">UPI (GPay/PhonePe/Paytm)</option>
                    <option value="Cash">Cash</option>
                    <option value="Bank Transfer">Bank Transfer / NEFT</option>
                    <option value="Cheque">Cheque</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              )}
            </div>

            {/* India extra fields */}
            {mode === 'india' && (
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' }}>
                <div>
                  <label style={lbl}>Transaction Reference (optional)</label>
                  <input style={inp} type="text" placeholder="UPI ref / UTR number" value={manualRef} onChange={e=>setManualRef(e.target.value)} />
                  <div style={{ fontSize:'10px', color:'rgba(var(--fg-rgb),var(--a25))', marginTop:'4px' }}>If filled → marked as Paid automatically</div>
                </div>
                <div>
                  <label style={lbl}>Payment Date (optional)</label>
                  <input style={inp} type="date" value={manualDate} onChange={e=>setManualDate(e.target.value)} />
                </div>
              </div>
            )}

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' }}>
              <div>
                <label style={lbl}>Due Date (optional)</label>
                <input style={inp} type="date" value={dueDate} onChange={e=>setDueDate(e.target.value)} />
              </div>
              <div>
                <label style={lbl}>Notes (optional)</label>
                <input style={inp} type="text" placeholder="e.g. May 2026 fee" value={notes} onChange={e=>setNotes(e.target.value)} />
              </div>
            </div>

            {err && <div style={{ marginTop:'10px', padding:'9px 13px', background:'rgba(220,60,60,0.1)', border:'0.5px solid rgba(220,60,60,0.3)', borderRadius:'8px', fontSize:'13px', color:'#f09595' }}>{err}</div>}
            {ok  && <div style={{ marginTop:'10px', padding:'9px 13px', background:'rgba(16,185,129,0.1)', border:'0.5px solid rgba(16,185,129,0.3)', borderRadius:'8px', fontSize:'13px', color:'#34d399' }}>{ok}</div>}

            <button type="submit" disabled={busy} style={{ width:'100%', padding:'14px', background: busy ? 'rgba(100,100,100,0.3)' : mode==='international' ? 'linear-gradient(135deg,#1877f2,#0ea5e9)' : 'linear-gradient(135deg,#f4a335,#e87c1e)', color:'#fff', fontSize:'15px', fontWeight:800, border:'none', borderRadius:'10px', cursor:busy?'not-allowed':'pointer', marginTop:'14px', fontFamily:'inherit', display:'flex', alignItems:'center', justifyContent:'center', gap:'8px' }}>
              {busy
                ? <><span style={{ display:'inline-block', width:'16px', height:'16px', border:'2px solid rgba(var(--fg-rgb),var(--a30))', borderTopColor:'#fff', borderRadius:'50%', animation:'spin .7s linear infinite' }}/> Processing...</>
                : mode === 'international'
                  ? '🅿️ Generate PayPal Link'
                  : '📝 Record Manual Payment'
              }
            </button>
          </form>

          {/* Result */}
          {generated && (
            <div style={{ marginTop:'18px', padding:'16px', background:'rgba(16,185,129,0.07)', border:'1px solid rgba(16,185,129,0.25)', borderRadius:'14px' }}>
              <div style={{ fontSize:'13px', fontWeight:700, color:'#34d399', marginBottom:'10px' }}>
                ✅ {generated.type==='paypal' ? 'PayPal Link Ready' : generated.type==='manual_link' ? 'Payment Link Saved' : 'Payment Recorded'} — {generated.invoiceNo}
              </div>
              <div style={{ fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a50))', marginBottom:'10px' }}>
                <span style={{ color:'rgba(var(--fg-rgb),var(--a80))', fontWeight:600 }}>{generated.studentName}</span>
                {' · '}
                <span style={{ color:'#10b981', fontWeight:700 }}>
                  {generated.currency==='INR' ? `₹${parseFloat(generated.amount).toLocaleString('en-IN')}` : `$${generated.amount} ${generated.currency}`}
                </span>
                {' · '}{generated.course}
                {generated.type==='manual' && <span style={{ marginLeft:'8px', fontSize:'10px', padding:'2px 8px', borderRadius:'8px', background:generated.status==='paid'?'rgba(16,185,129,0.2)':'rgba(244,163,53,0.2)', color:generated.status==='paid'?'#10b981':'#f4a335' }}>{generated.status}</span>}
              </div>

              {(generated.type === 'paypal' || generated.type === 'manual_link') && (
                <>
                  <div style={{ background:'rgba(0,0,0,0.35)', borderRadius:'8px', padding:'10px 13px', fontSize:'11px', fontFamily:'monospace', color:'#5aabff', wordBreak:'break-all', marginBottom:'12px', border:'0.5px solid rgba(30,144,255,0.2)' }}>
                    {generated.link}
                  </div>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'8px' }}>
                    <button type="button" onClick={()=>copyText(generated.link,'link')} style={{ padding:'10px 8px', fontSize:'12px', fontWeight:700, borderRadius:'9px', border:'none', cursor:'pointer', background:copied==='link'?'rgba(16,185,129,0.25)':'rgba(var(--fg-rgb),0.08)', color:copied==='link'?'#34d399':'rgba(var(--fg-rgb),var(--a70))', fontFamily:'inherit' }}>
                      {copied==='link'?'✓ Copied!':'📋 Copy Link'}
                    </button>
                    <a href={whatsappMsg(generated.link, generated.studentName, generated.amount, generated.currency, generated.course)}
                      target="_blank" rel="noreferrer"
                      style={{ padding:'10px 8px', fontSize:'12px', fontWeight:700, borderRadius:'9px', background:'rgba(37,211,102,0.18)', color:'#25d366', textAlign:'center', textDecoration:'none', display:'flex', alignItems:'center', justifyContent:'center' }}>
                      📱 WhatsApp
                    </a>
                    <button type="button" onClick={()=>copyText(`${BUSINESS_NAME}\nInvoice: ${generated.invoiceNo}\nStudent: ${generated.studentName}\nCourse: ${generated.course}\nAmount: $${generated.amount} ${generated.currency}\n\nPay: ${generated.link}`,'inv')}
                      style={{ padding:'10px 8px', fontSize:'12px', fontWeight:700, borderRadius:'9px', border:'none', cursor:'pointer', background:copied==='inv'?'rgba(139,92,246,0.3)':'rgba(139,92,246,0.12)', color:copied==='inv'?'#c4b5fd':'#a78bfa', fontFamily:'inherit' }}>
                      {copied==='inv'?'✓ Copied!':'📝 Invoice'}
                    </button>
                  </div>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px', marginTop:'12px', alignItems:'center' }}>
                    <div style={{ textAlign:'center', padding:'10px', background:'rgba(var(--fg-rgb),0.05)', borderRadius:'12px', border:'1px solid rgba(var(--fg-rgb),0.08)' }}>
                      <img src={qrCodeSrc(generated.link)} alt="QR code" style={{ width:'140px', height:'140px', borderRadius:'14px' }} />
                      <div style={{ marginTop:'8px', fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a50))' }}>Scan to pay</div>
                    </div>
                    <button type="button" onClick={() => setPreviewInvoice(generated)} style={{ width:'100%', padding:'12px 14px', fontSize:'12px', fontWeight:700, borderRadius:'12px', border:'none', cursor:'pointer', background:'rgba(96,165,250,0.16)', color:'#5aabff', fontFamily:'inherit' }}>
                      🔍 Preview Invoice
                    </button>
                  </div>
                </>
              )}
              {generated.type === 'manual' && (
                <div style={{ fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a40))', lineHeight:1.8 }}>
                  {generated.method && <div>📱 Method: <strong style={{ color:'rgba(var(--fg-rgb),var(--a70))' }}>{generated.method}</strong></div>}
                  {generated.ref    && <div>🔖 Reference: <strong style={{ color:'rgba(var(--fg-rgb),var(--a70))' }}>{generated.ref}</strong></div>}
                </div>
              )}
            </div>
          )}

          {previewInvoice && (
            <div style={{ marginTop:'18px', padding:'16px', background:'rgba(var(--fg-rgb),0.04)', border:'1px solid rgba(var(--fg-rgb),0.08)', borderRadius:'14px' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:'12px', marginBottom:'14px', flexWrap:'wrap' }}>
                <div>
                  <div style={{ fontSize:'13px', fontWeight:700, color:'#5aabff' }}>Invoice Preview</div>
                  <div style={{ fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a50))' }}>Invoice No: {previewInvoice.invoiceNo}</div>
                </div>
                <div style={{ display:'flex', gap:'8px', flexWrap:'wrap' }}>
                  <button type="button" onClick={() => printInvoice(previewInvoice)} style={{ padding:'10px 14px', fontSize:'12px', fontWeight:700, borderRadius:'9px', border:'none', cursor:'pointer', background:'rgba(16,185,129,0.12)', color:'#10b981', fontFamily:'inherit' }}>
                    🖨 Print
                  </button>
                  <button type="button" onClick={() => setPreviewInvoice(null)} style={{ padding:'10px 14px', fontSize:'12px', fontWeight:700, borderRadius:'9px', border:'none', cursor:'pointer', background:'rgba(var(--fg-rgb),0.08)', color:'var(--fg-solid)', fontFamily:'inherit' }}>
                    ✕ Close
                  </button>
                </div>
              </div>
              <div style={{ color:'rgba(var(--fg-rgb),var(--a85))', fontSize:'13px', marginBottom:'10px' }}>{previewInvoice.studentName || '—'}</div>
              <div style={{ display:'grid', gap:'8px' }}>
                <div><strong>Course:</strong> {previewInvoice.course || '—'}</div>
                <div><strong>Amount:</strong> {previewInvoice.currency==='INR' ? `₹${previewInvoice.amount}` : `${previewInvoice.currency} ${previewInvoice.amount}`}</div>
                <div><strong>Status:</strong> {previewInvoice.status || 'Pending'}</div>
                <div><strong>Type:</strong> {previewInvoice.type === 'paypal' ? 'PayPal Link' : 'Manual Payment'}</div>
                {previewInvoice.method && <div><strong>Method:</strong> {previewInvoice.method}</div>}
                {previewInvoice.ref && <div><strong>Reference:</strong> {previewInvoice.ref}</div>}
                {previewInvoice.notes && <div><strong>Notes:</strong> {previewInvoice.notes}</div>}
              </div>
            </div>
          )}
        </div>
      )}

      {tab === 'reports' && (
        <div style={{ background:'rgba(var(--fg-rgb),0.04)', border:'0.5px solid rgba(var(--fg-rgb),0.07)', borderRadius:'14px', padding:'1.2rem' }}>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))', gap:'12px', marginBottom:'18px' }}>
            <div style={{ padding:'14px', borderRadius:'12px', background:'rgba(16,185,129,0.08)', border:'1px solid rgba(16,185,129,0.18)' }}>
              <div style={{ fontSize:'11px', fontWeight:700, color:'#10b981', marginBottom:'8px' }}>Total Payments</div>
              <div style={{ fontSize:'24px', fontWeight:800, color:'var(--fg-solid)' }}>{payments.length}</div>
            </div>
            <div style={{ padding:'14px', borderRadius:'12px', background:'rgba(24,119,242,0.08)', border:'1px solid rgba(30,144,255,0.18)' }}>
              <div style={{ fontSize:'11px', fontWeight:700, color:'#5aabff', marginBottom:'8px' }}>Total Collected</div>
              <div style={{ fontSize:'24px', fontWeight:800, color:'var(--fg-solid)' }}>₹{totalPaid.toLocaleString('en-IN')}</div>
            </div>
            <div style={{ padding:'14px', borderRadius:'12px', background:'rgba(232,124,30,0.08)', border:'1px solid rgba(232,124,30,0.18)' }}>
              <div style={{ fontSize:'11px', fontWeight:700, color:'#f4a335', marginBottom:'8px' }}>Pending Amount</div>
              <div style={{ fontSize:'24px', fontWeight:800, color:'var(--fg-solid)' }}>₹{totalDue.toLocaleString('en-IN')}</div>
            </div>
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px', marginBottom:'18px' }}>
            <div style={{ padding:'14px', borderRadius:'12px', background:'rgba(var(--fg-rgb),0.03)' }}>
              <div style={{ fontSize:'12px', fontWeight:700, color:'rgba(var(--fg-rgb),var(--a80))', marginBottom:'10px' }}>Status Breakdown</div>
              {['paid','pending','overdue'].map(status => (
                <div key={status} style={{ display:'flex', justifyContent:'space-between', padding:'8px 0', borderBottom:'1px solid rgba(var(--fg-rgb),0.06)', color:'rgba(var(--fg-rgb),var(--a70))' }}>
                  <span style={{ textTransform:'capitalize' }}>{status}</span>
                  <span>{payments.filter(p=>p.status===status).length}</span>
                </div>
              ))}
            </div>
            <div style={{ padding:'14px', borderRadius:'12px', background:'rgba(var(--fg-rgb),0.03)' }}>
              <div style={{ fontSize:'12px', fontWeight:700, color:'rgba(var(--fg-rgb),var(--a80))', marginBottom:'10px' }}>Currency Totals</div>
              {Object.entries(payments.reduce((acc,p)=>{
                const key = p.currency||'INR'
                acc[key] = (acc[key]||0) + (p.amount||0)
                return acc
              }, {})).map(([currency, total]) => (
                <div key={currency} style={{ display:'flex', justifyContent:'space-between', padding:'8px 0', borderBottom:'1px solid rgba(var(--fg-rgb),0.06)', color:'rgba(var(--fg-rgb),var(--a70))' }}>
                  <span>{currency}</span>
                  <span>{currency==='INR' ? `₹${total.toLocaleString('en-IN')}` : `${currency} ${total.toFixed(2)}`}</span>
                </div>
              ))}
            </div>
          </div>

          <button type="button" onClick={downloadReport} style={{ padding:'12px 18px', borderRadius:'10px', border:'none', cursor:'pointer', background:'linear-gradient(135deg,#1e90ff,#0ea5e9)', color:'#fff', fontSize:'13px', fontWeight:700, fontFamily:'inherit' }}>
            📥 Download Payments Report
          </button>
        </div>
      )}

      {/* ══ PAYMENTS LIST ══ */}
      {tab === 'list' && (
        <div style={{ background:'rgba(var(--fg-rgb),0.04)', border:'0.5px solid rgba(var(--fg-rgb),0.07)', borderRadius:'14px', padding:'1.1rem 1.2rem' }}>
          <div style={{ display:'flex', gap:'6px', marginBottom:'14px', flexWrap:'wrap' }}>
            {[['all','All'],['pending','Pending'],['paid','Paid'],['overdue','Overdue']].map(([f,l])=>(
              <button key={f} onClick={()=>setFilter(f)} style={{ fontSize:'11px', fontWeight:600, padding:'5px 14px', borderRadius:'20px', border:'none', cursor:'pointer', fontFamily:'inherit', background:filter===f?'#1e90ff':'rgba(var(--fg-rgb),0.07)', color:filter===f?'#fff':'rgba(var(--fg-rgb),var(--a45))' }}>
                {l} {f!=='all'&&<span style={{opacity:0.6}}>({payments.filter(p=>p.status===f).length})</span>}
              </button>
            ))}
          </div>

          {ok && <div style={{ marginBottom:'10px', padding:'9px 13px', background:'rgba(16,185,129,0.1)', border:'0.5px solid rgba(16,185,129,0.3)', borderRadius:'8px', fontSize:'13px', color:'#34d399' }}>{ok}</div>}

          {loading ? (
            <div style={{ textAlign:'center', padding:'2.5rem', color:'rgba(var(--fg-rgb),var(--a20))', fontSize:'13px' }}>Loading...</div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign:'center', padding:'2.5rem 1rem' }}>
              <div style={{ fontSize:'32px', marginBottom:'10px' }}>💳</div>
              <div style={{ fontSize:'13px', color:'rgba(var(--fg-rgb),var(--a30))', marginBottom:'8px' }}>No records yet.</div>
              <button onClick={()=>setTab('generate')} style={{ fontSize:'12px', fontWeight:600, padding:'8px 18px', borderRadius:'10px', background:'rgba(30,144,255,0.15)', color:'#5aabff', border:'none', cursor:'pointer', fontFamily:'inherit' }}>+ New Payment</button>
            </div>
          ) : (
            <div style={{ display:'grid', gap:'8px' }}>
              {filtered.map(p=>{
                const isIndia = p.currency === 'INR' || p.gateway === 'manual'
                const displayAmt = isIndia
                  ? `₹${(p.amount||0).toLocaleString('en-IN')}`
                  : `$${(p.amount||0).toFixed(2)} ${p.currency||'USD'}`
                return (
                  <div key={p.id} style={{ padding:'12px 14px', background:'rgba(var(--fg-rgb),0.03)', borderRadius:'10px', borderLeft:`3px solid ${SC[p.status]||'rgba(var(--fg-rgb),0.15)'}` }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:'10px' }}>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ display:'flex', alignItems:'center', gap:'6px', flexWrap:'wrap', marginBottom:'3px' }}>
                          <span style={{ fontSize:'13px', fontWeight:700, color:'rgba(var(--fg-rgb),var(--a85))' }}>{p.student_name||'—'}</span>
                          {p.invoice_no&&<span style={{ fontSize:'9px', fontWeight:700, padding:'2px 7px', borderRadius:'8px', background:'rgba(232,124,30,0.15)', color:'#e87c1e' }}>{p.invoice_no}</span>}
                          <span style={{ fontSize:'9px', fontWeight:700, padding:'2px 7px', borderRadius:'8px', background:isIndia?'rgba(244,163,53,0.15)':'rgba(24,119,242,0.15)', color:isIndia?'#f4a335':'#5aabff' }}>
                            {isIndia ? '🇮🇳 Manual' : '🌍 PayPal'}
                          </span>
                        </div>
                        <div style={{ fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a35))' }}>
                          {p.course_name}{p.due_date&&` · Due: ${p.due_date}`}{p.paid_date&&` · Paid: ${p.paid_date}`}
                        </div>
                        {p.notes&&<div style={{ fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a22))', marginTop:'2px' }}>{p.notes.split('\n')[0]}</div>}
                      </div>
                      <div style={{ textAlign:'right', flexShrink:0 }}>
                        <div style={{ fontSize:'16px', fontWeight:800, color:SC[p.status]||'var(--fg-solid)' }}>{displayAmt}</div>
                        <span style={{ fontSize:'9px', fontWeight:700, padding:'2px 8px', borderRadius:'10px', background:SB[p.status]||'rgba(var(--fg-rgb),0.05)', color:SC[p.status]||'#aaa', textTransform:'uppercase' }}>{p.status}</span>
                      </div>
                    </div>
                    <div style={{ display:'flex', gap:'6px', marginTop:'10px', flexWrap:'wrap' }}>
                      {p.payment_link&&<>
                        <button type="button" onClick={()=>copyText(payLink(p),p.id)} style={{ fontSize:'10px', fontWeight:700, padding:'5px 10px', borderRadius:'7px', border:'none', cursor:'pointer', background:copied===p.id?'rgba(16,185,129,0.2)':'rgba(var(--fg-rgb),0.07)', color:copied===p.id?'#34d399':'rgba(var(--fg-rgb),var(--a60))', fontFamily:'inherit' }}>
                          {copied===p.id?'✓ Copied':'📋 Copy'}
                        </button>
                        <a href={whatsappMsg(payLink(p),p.student_name,(p.amount||0).toFixed(2),p.currency||'USD',p.course_name)}
                          target="_blank" rel="noreferrer"
                          style={{ fontSize:'10px', fontWeight:700, padding:'5px 10px', borderRadius:'7px', background:'rgba(37,211,102,0.15)', color:'#25d366', textDecoration:'none', display:'inline-flex', alignItems:'center', gap:'3px' }}>
                          📱 WhatsApp
                        </a>
                        <a href={payLink(p)} target="_blank" rel="noreferrer"
                          style={{ fontSize:'10px', fontWeight:700, padding:'5px 10px', borderRadius:'7px', background:'rgba(24,119,242,0.12)', color:'#5aabff', textDecoration:'none' }}>
                          🅿️ Open
                        </a>
                      </>}
                      {p.invoice_no && (
                        <button type="button" onClick={() => printInvoice({ invoiceNo: p.invoice_no, studentName: p.student_name, course: p.course_name, amount: p.amount, currency: p.currency, status: p.status, type: p.gateway === 'paypal' ? 'paypal' : 'manual', notes: p.notes, link: p.payment_link ? payLink(p) : null })} style={{ fontSize:'10px', fontWeight:700, padding:'5px 10px', borderRadius:'7px', border:'none', cursor:'pointer', background:'rgba(96,165,250,0.12)', color:'#5aabff', fontFamily:'inherit' }}>
                          🖨 Print
                        </button>
                      )}
                      {p.status!=='paid'&&(
                        <button type="button" onClick={()=>markPaid(p.id)} style={{ fontSize:'10px', fontWeight:700, padding:'5px 10px', borderRadius:'7px', border:'none', cursor:'pointer', background:'rgba(16,185,129,0.12)', color:'#10b981', fontFamily:'inherit' }}>
                          ✓ Mark Paid
                        </button>
                      )}
                      <button type="button" onClick={()=>deletePay(p.id)} style={{ fontSize:'10px', fontWeight:700, padding:'5px 10px', borderRadius:'7px', border:'none', cursor:'pointer', background:'rgba(239,68,68,0.1)', color:'#f87171', fontFamily:'inherit', marginLeft:'auto' }}>✕</button>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  )
}
